import { Injectable } from '@angular/core';
import { CsmsNode } from 'api/charging_stations.api';
import { take } from 'lodash';
import { BehaviorSubject, combineLatest, from, Observable, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, switchMap, withLatestFrom } from 'rxjs/operators';
import { Ticket } from '@kempoweroyj/ngx-ticketing-api-client';
import { CeLocationRow } from 'shared/stations-service/locations.service';
import {
    SearchApiService,
    GlobalSearchConnectorResult,
    GlobalSearchLocationResult,
    GlobalSearchStationResult,
    GlobalSearchOrganizationResult,
} from '@kempoweroyj/ngx-search-api-client';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Evse } from '@kempoweroyj/ngx-evse-api-client';
import { AuthService } from 'providers/auth.service';
import { SearchHistoryService } from './search-history.service';

const MAX_RESULTS_DISPLAYED = 25;

export interface LoadingSearchResult {
    type: 'loading';
}

export type SearchResult =
    | StationSearchResult
    | ConnectorSearchResult
    | TicketSearchResult
    | NoSearchResult
    | LocationSearchResult
    | OrganizationSearchResult
    | LoadingSearchResult;

export interface StationSearchResult {
    type: 'station';
    station?: CsmsNode;
    globalSearchStation?: GlobalSearchStationResult;
}
export interface ConnectorSearchResult {
    type: 'connector';
    station?: CsmsNode;
    connector?: Evse;
    globalSearchConnector?: GlobalSearchConnectorResult;
}
export interface TicketSearchResult {
    type: 'ticket';
    ticket?: Ticket;
}
export interface LocationSearchResult {
    type: 'location';
    location?: CeLocationRow;
    globalSearchLocation?: GlobalSearchLocationResult;
}
export interface OrganizationSearchResult {
    type: 'organization';
    globalSearchOrganization?: GlobalSearchOrganizationResult;
}

export interface NoSearchResult {
    type: 'no_results';
}

export type DisplayResult = SearchResult;

@Injectable({
    providedIn: 'root',
})
@UntilDestroy()
export class SearchService {
    results$: Observable<DisplayResult[]>;
    isLoading$ = new BehaviorSubject<boolean>(false);
    isShowingHistory$ = new BehaviorSubject<boolean>(false);
    private text$ = new Subject<string>();
    private isActive$ = new BehaviorSubject<boolean>(false);
    private hasAccessTo$ = new Observable<{ locations: boolean; organizations: boolean }>();

    hasAccessTo = {
        locations: this.authService.hasAccessTo$(['fleet.group.read']),
        organizations: this.authService.profile$.pipe(
            switchMap(() => {
                const privileges = this.authService.getPermissions();
                return privileges.isSystemAdmin || privileges.isOrganizationAdmin ? of(true) : of(false);
            })
        ),
    };
    constructor(
        private searchApi: SearchApiService,
        private authService: AuthService,
        private historyService: SearchHistoryService
    ) {
        this.hasAccessTo$ = combineLatest([this.hasAccessTo.locations, this.hasAccessTo.organizations]).pipe(
            switchMap(([locations, organizations]) => {
                return of({ locations, organizations });
            })
        );

        this.results$ = combineLatest([
            this.text$.pipe(debounceTime(500), distinctUntilChanged()),
            this.hasAccessTo$,
            this.isActive$,
            this.historyService.getHistory$(),
        ]).pipe(
            // discard any ongoing search if there are new text
            switchMap(([text, , isActive, history]) => {
                if (!text?.trim().length) {
                    this.isLoading$.next(false);
                    // Show history when search is active but no text
                    if (isActive && history.length > 0) {
                        this.isShowingHistory$.next(true);
                        return of(history.map((item) => item.result));
                    }
                    this.isShowingHistory$.next(false);
                    return of(null);
                }
                this.isLoading$.next(true);
                this.isShowingHistory$.next(false);
                // Emit loading placeholder immediately, then API results
                return from(this.searchApi.globalSearch({ query: text.trim() })).pipe(
                    startWith({ type: 'loading-placeholder' as const })
                );
            }),
            withLatestFrom(this.hasAccessTo$),
            map(([res, hasAccessTo]) => {
                if (!res) return [];
                // Check if this is already a DisplayResult array (history)
                if (Array.isArray(res)) {
                    return res as DisplayResult[];
                }
                // Check if this is a loading placeholder
                if ('type' in res && res.type === 'loading-placeholder') {
                    return [{ type: 'loading' }] as DisplayResult[];
                }
                // At this point, res is the API response
                const apiResponse = res as unknown as {
                    results: Array<
                        | GlobalSearchLocationResult
                        | GlobalSearchStationResult
                        | GlobalSearchConnectorResult
                        | GlobalSearchOrganizationResult
                    >;
                };
                this.isLoading$.next(false);
                const globalSearchResults: SearchResult[] = [];
                for (const item of apiResponse.results) {
                    if (item.type === 'location' && hasAccessTo?.locations) {
                        globalSearchResults.push({
                            type: item.type,
                            globalSearchLocation: item as GlobalSearchLocationResult,
                        });
                    } else if (item.type === 'station') {
                        globalSearchResults.push({
                            type: item.type,
                            globalSearchStation: item as GlobalSearchStationResult,
                        });
                    } else if (item.type === 'connector') {
                        globalSearchResults.push({
                            type: item.type,
                            globalSearchConnector: item as GlobalSearchConnectorResult,
                        });
                    } else if (item.type === 'organization' && hasAccessTo?.organizations) {
                        globalSearchResults.push({
                            type: item.type,
                            globalSearchOrganization: item as GlobalSearchOrganizationResult,
                        });
                    }
                }
                if (globalSearchResults.length === 0) {
                    globalSearchResults.push({ type: 'no_results' });
                }
                return take(globalSearchResults, MAX_RESULTS_DISPLAYED);
            })
        );
    }

    search(text: string) {
        this.text$.next(text);
    }

    setActive(isActive: boolean) {
        this.isActive$.next(isActive);
        if (isActive) {
            // Trigger empty search to show history
            this.text$.next('');
        }
    }

    addToHistory(result: SearchResult) {
        this.historyService.addToHistory(result);
    }

    removeFromHistory(result: SearchResult) {
        this.historyService.removeFromHistory(result);
    }

    clearHistory() {
        this.historyService.clearHistory();
    }

    clear() {
        this.text$.next(null);
    }
}
